import { Component, OnInit, OnDestroy, signal, inject, effect } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

// PrimeNG imports
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { PasswordModule } from 'primeng/password';
import { CheckboxModule } from 'primeng/checkbox';
import { PanelModule } from 'primeng/panel';

// API imports
import { Api } from '../../../api/api';
import {
  adminUsersControllerGetAvailablePermissions,
  adminUsersControllerGetAdminUserById,
  adminUsersControllerCreateAdminUser,
  adminUsersControllerUpdateAdminUser,
} from '../../../api/functions';
import { UserResponseAdminAdminDto } from '../../../api/models/user-response-admin-admin-dto';
import { CreateAdminUserDto } from '../../../api/models/create-admin-user-dto';
import { UpdateAdminUserDto } from '../../../api/models/update-admin-user-dto';

// Services
import { HeaderService } from '../../../services/header.service';

// Validators
import { passwordMatchValidator } from '../../../shared/validators/password-match.validator';

// Type extracted from API response - matches the inline type from the generated function
export type Permission = {
  value?: string;
  label?: string;
  category?: string;
  description?: string;
};

// Extract the permission type from the API model dynamically
type AdminPermission = UserResponseAdminAdminDto['adminPermissions'][number];

@Component({
  selector: 'app-admin-user-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    CardModule,
    InputTextModule,
    ButtonModule,
    MessageModule,
    SkeletonModule,
    ToastModule,
    PasswordModule,
    CheckboxModule,
    PanelModule,
  ],
  providers: [MessageService],
  templateUrl: './admin-user-edit.component.html',
  styleUrl: './admin-user-edit.component.css',
})
export class AdminUserEditComponent implements OnInit, OnDestroy {
  // Inject dependencies using modern inject() function
  private readonly api = inject(Api);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly headerService = inject(HeaderService);

  // Loading states
  protected readonly loading = signal(true);
  protected readonly submitting = signal(false);
  protected readonly formValid = signal(false);

  // Permissions data
  protected readonly permissions = signal<Permission[]>([]);

  // Route and mode state
  protected userId: string | null = null;
  protected isEditMode = false;

  // Define individual form controls
  emailControl = new FormControl('', [Validators.required, Validators.email]);
  usernameControl = new FormControl('', [Validators.required, Validators.minLength(3)]);
  passwordControl = new FormControl('');
  passwordConfirmControl = new FormControl('');
  permissionsControl = new FormControl<string[]>([]);

  // Create form group using the individual controls
  userForm = new FormGroup(
    {
      email: this.emailControl,
      username: this.usernameControl,
      password: this.passwordControl,
      passwordConfirm: this.passwordConfirmControl,
      permissions: this.permissionsControl,
    },
    { validators: passwordMatchValidator }
  );

  constructor() {
    // Track form validity changes
    this.userForm.statusChanges.subscribe(() => {
      this.formValid.set(this.userForm.valid);
    });

    // Automatically update header buttons when form validity or submitting state changes
    effect(() => {
      // Only update buttons if header is not in loading state
      if (!this.headerService.loading()) {
        this.updateHeaderButtons();
      }
    });
  }

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id');
    // Only edit mode if userId exists and is not 'new'
    this.isEditMode = this.userId !== null && this.userId !== 'new';

    // Show header skeletons while loading
    this.headerService.setLoading(true);

    // Setup static header content (title and subtitle)
    this.setupHeader();

    // Set password field requirements based on mode
    if (this.isEditMode) {
      // Edit mode: passwords optional (no validators)
      this.setPasswordValidators(false);
    } else {
      // Create mode: passwords required
      this.setPasswordValidators(true);
    }

    // Load data
    this.loadData();
  }

  ngOnDestroy(): void {
    this.headerService.clear();
  }

  /**
   * Setup static header content (title and subtitle)
   * Called once during ngOnInit
   */
  private setupHeader(): void {
    if (this.isEditMode) {
      this.headerService.setTitle(this.translateService.instant('adminUserEdit.editTitle'));
      this.headerService.setSubtitle(this.translateService.instant('adminUserEdit.editSubtitle'));
    } else {
      this.headerService.setTitle(this.translateService.instant('adminUserEdit.createTitle'));
      this.headerService.setSubtitle(this.translateService.instant('adminUserEdit.createSubtitle'));
    }
  }

  /**
   * Update header action buttons based on current state
   * Automatically called via effect() when signals change
   */
  private updateHeaderButtons(): void {
    this.headerService.setButtons([
      {
        label: this.translateService.instant('common.cancel'),
        icon: 'pi pi-times',
        severity: 'secondary',
        outlined: true,
        action: () => this.onCancel(),
      },
      {
        label: this.translateService.instant(this.isEditMode ? 'common.update' : 'common.create'),
        icon: this.isEditMode ? 'pi pi-save' : 'pi pi-plus',
        severity: 'primary',
        disabled: !this.formValid(),
        loading: this.submitting(),
        action: () => this.onSubmit(),
      },
    ]);
  }

  /**
   * Load all necessary data (permissions and user data if editing)
   */
  private async loadData(): Promise<void> {
    try {
      // Load available permissions first
      await this.loadAvailablePermissions();

      // Load user data if in edit mode (userId exists and is not 'new')
      if (this.isEditMode && this.userId && this.userId !== 'new') {
        await this.loadAdminUser(this.userId);
      }
    } finally {
      // Hide skeletons and show buttons
      this.headerService.setLoading(false);
      this.loading.set(false);
    }
  }

  /**
   * Load available permissions from the backend API
   */
  private async loadAvailablePermissions(): Promise<void> {
    try {
      // Call the API using the generated function and Api service
      const response = await this.api.invoke(adminUsersControllerGetAvailablePermissions);

      // Update the permissions signal with the response data
      if (response.permissions) {
        this.permissions.set(response.permissions);
      }
    } catch (error) {
      // Error already displayed by interceptor
      // Just prevent the error from bubbling up
    }
  }

  /**
   * Set or remove password validators
   */
  private setPasswordValidators(required: boolean): void {
    if (required) {
      this.passwordControl.setValidators([Validators.required, Validators.minLength(8)]);
      this.passwordConfirmControl.setValidators([Validators.required]);
    } else {
      this.passwordControl.clearValidators();
      this.passwordConfirmControl.clearValidators();
    }
    this.passwordControl.updateValueAndValidity();
    this.passwordConfirmControl.updateValueAndValidity();
  }

  /**
   * Group permissions by category for display
   * Returns a Map with category as key and array of permissions as value
   */
  getPermissionsByCategory(): Map<string, Permission[]> {
    const grouped = new Map<string, Permission[]>();

    this.permissions().forEach((permission) => {
      const category = permission.category || 'Other';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(permission);
    });

    return grouped;
  }

  /**
   * Get all unique categories from permissions
   */
  protected getCategories(): string[] {
    return Array.from(this.getPermissionsByCategory().keys());
  }

  /**
   * Get permissions for a specific category
   */
  protected getPermissionsForCategory(category: string): Permission[] {
    return this.getPermissionsByCategory().get(category) || [];
  }

  /**
   * Check if a permission is currently selected
   */
  protected isPermissionSelected(permissionValue: string): boolean {
    const selectedPermissions = this.permissionsControl.value || [];
    return selectedPermissions.includes(permissionValue);
  }

  /**
   * Toggle a permission on/off
   */
  protected togglePermission(permissionValue: string, checked: boolean): void {
    const currentPermissions = this.permissionsControl.value || [];

    if (checked) {
      // Add permission if not already present
      if (!currentPermissions.includes(permissionValue)) {
        this.permissionsControl.setValue([...currentPermissions, permissionValue]);
      }
    } else {
      // Remove permission
      this.permissionsControl.setValue(currentPermissions.filter((p) => p !== permissionValue));
    }
  }

  /**
   * Load admin user data from the backend
   */
  private async loadAdminUser(userId: string): Promise<void> {
    try {
      // Call the API to get user data
      const user: UserResponseAdminAdminDto = await this.api.invoke(
        adminUsersControllerGetAdminUserById,
        { id: userId }
      );

      // Populate form with admin user data including permissions
      this.userForm.patchValue({
        email: user.email,
        username: user.username,
        permissions: user.adminPermissions || [],
      });
    } catch (error) {
      // Error already displayed by interceptor
      // Navigate back to list if user not found
      this.router.navigate(['/admin-users']);
    }
  }

  protected async onSubmit(): Promise<void> {
    if (this.userForm.valid && !this.submitting()) {
      this.submitting.set(true);

      try {
        const formValue = this.userForm.value;
        // Extract permissions with proper type (dynamically derived from API model)
        const permissions = (formValue.permissions || []) as AdminPermission[];

        if (this.isEditMode && this.userId) {
          // Update existing user
          const updateData: UpdateAdminUserDto = {
            email: formValue.email ?? undefined,
            username: formValue.username ?? undefined,
            adminPermissions: permissions,
          };

          // Note: UpdateAdminUserDto doesn't have password field
          // Password updates should be handled separately if needed

          await this.api.invoke(adminUsersControllerUpdateAdminUser, {
            id: this.userId,
            body: updateData,
          });

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('adminUserEdit.success.editTitle'),
            detail: this.translateService.instant('adminUserEdit.success.editMessage'),
            life: 4000,
          });
        } else {
          // Create new user
          const createData: CreateAdminUserDto = {
            email: formValue.email!,
            username: formValue.username!,
            password: formValue.password!,
            adminPermissions: permissions,
          };

          await this.api.invoke(adminUsersControllerCreateAdminUser, {
            body: createData,
          });

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('adminUserEdit.success.createTitle'),
            detail: this.translateService.instant('adminUserEdit.success.createMessage'),
            life: 4000,
          });
        }

        // Navigate back to admin-users page
        this.router.navigate(['/admin-users']);
      } catch (error) {
        // Error already displayed by interceptor
        // Just prevent the error from bubbling up
      } finally {
        this.submitting.set(false);
      }
    } else if (!this.userForm.valid) {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched();
    }
  }

  protected onCancel(): void {
    this.router.navigate(['/admin-users']);
  }

  protected getEmailErrorMessage(): string {
    if (this.emailControl.errors?.['required']) {
      return this.translateService.instant('adminUserEdit.errors.emailRequired');
    }
    if (this.emailControl.errors?.['email']) {
      return this.translateService.instant('adminUserEdit.errors.emailInvalid');
    }
    return '';
  }

  protected getUsernameErrorMessage(): string {
    if (this.usernameControl.errors?.['required']) {
      return this.translateService.instant('adminUserEdit.errors.usernameRequired');
    }
    if (this.usernameControl.errors?.['minlength']) {
      return this.translateService.instant('adminUserEdit.errors.usernameMinLength');
    }
    return '';
  }

  protected getPasswordErrorMessage(): string {
    if (this.passwordControl.errors?.['required']) {
      return this.translateService.instant('adminUserEdit.errors.passwordRequired');
    }
    if (this.passwordControl.errors?.['minlength']) {
      return this.translateService.instant('adminUserEdit.errors.passwordMinLength');
    }
    return '';
  }

  protected getPasswordConfirmErrorMessage(): string {
    if (this.passwordConfirmControl.errors?.['required']) {
      return this.translateService.instant('adminUserEdit.errors.passwordConfirmRequired');
    }
    if (this.userForm.errors?.['passwordMismatch'] && this.passwordConfirmControl.touched) {
      return this.translateService.instant('adminUserEdit.errors.passwordMismatch');
    }
    return '';
  }

  private markFormGroupTouched(): void {
    this.emailControl.markAsTouched();
    this.usernameControl.markAsTouched();
    this.passwordControl.markAsTouched();
    this.passwordConfirmControl.markAsTouched();
  }
}
